import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface MinimaxHailuo02I2vParams {
  /**
   * 输入图像URL或Base64编码
   */
  first_frame_image: string;
  /**
   * 视频生成提示词，长度必须在0-2000个字符之间
   */
  prompt?: string;
  /**
   * 视频时长，支持6秒（所有分辨率）或10秒（仅512P/768P），默认6秒
   * @default 6
   * @values 6 | 10
   */
  duration?: 6 | 10;
  /**
   * 视频分辨率，默认为768P
   * @default "768P"
   * @values 512P | 768P | 1080P
   */
  resolution?: '512P' | '768P' | '1080P';
  /**
   * 是否启用提示词优化器，默认为true
   * @default true
   */
  prompt_optimizer?: boolean;
  /**
   * 是否启用快速预处理，默认为false
   * @default false
   */
  fast_pretreatment?: boolean;
}

export interface MinimaxHailuo02I2vResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * minimax_hailuo_02_i2v
 *
 * MiniMax Hailuo-02 Image-to-Video Generator
 * 
 * Image-to-video generation implementation for MiniMax Hailuo-02 model with camera control support.
 * 
 * Model: MiniMax-Hailuo-02
 * Features: 图像生成视频 (Image-to-Video)
 * Duration: 6秒（所有分辨率）或10秒（512P/768P）
 * Resolution: 512P, 768P (默认), 1080P
 * Camera Control: 支持摄像机运动控制
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, minimaxHailuo02I2v } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await minimaxHailuo02I2v({
 *   // 参数见 MinimaxHailuo02I2vParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 MinimaxHailuo02I2vParams 接口定义
 * @returns 生成的资源列表
 */
export async function minimaxHailuo02I2v(
  params: MinimaxHailuo02I2vParams,
): Promise<MinimaxHailuo02I2vResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'minimax_hailuo_02_i2v',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: MinimaxHailuo02I2vResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
